import Head from 'next/head';
import { ReactNode } from 'react';
import { Organization, Thing, WithContext } from 'schema-dts';

import { CHANNEL } from '@boss/constants/shared';
import { useRouter } from '@boss/hooks';
import { LocalizedSlugs, Navigation } from '@boss/types/corporate-jobs';

import { seoConfig } from '../../utils/options';

type Props<T extends Thing> = {
  navigation: Navigation | null;
  title?: string;
  description?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  children: ReactNode;
  imageSrc?: string | null;
  localizedSlugs: LocalizedSlugs;
  metaKeywords?: string[] | undefined;
  structuredData?: WithContext<T>[];
  slug?: string;
};

const getHost = () => process.env.NEXT_PUBLIC_HOST ?? '';

const noIndexing = process.env.NEXT_PUBLIC_NO_INDEXING === 'true';

const getRobots = (noFollow: boolean, noIndex: boolean) => {
  const followValue = noFollow || noIndexing ? 'nofollow' : 'follow';
  const indexValue = noIndex || noIndexing ? 'noindex' : 'index';

  return `${followValue}, ${indexValue}`;
};

const SEO = <T extends Thing>({
  title: initialTitle,
  localizedSlugs,
  description,
  imageSrc,
  noIndex = false,
  noFollow = false,
  children,
  metaKeywords,
  slug,
  navigation,
  structuredData,
}: Props<T>) => {
  const { titleSuffixContent } = seoConfig;
  const { locale } = useRouter();
  const host = getHost();
  const robots = getRobots(noFollow, noIndex);
  const title = initialTitle ? `${initialTitle} - ${titleSuffixContent}` : titleSuffixContent;
  const keywords = metaKeywords?.join(', ');

  const path: string = slug ? `${localizedSlugs[locale]}/${slug}` : localizedSlugs[locale];
  const buildUrl = (locale: string, path: string) => `${host}/${locale}/${path}`;
  const domainName = process.env.NEXT_PUBLIC_HOST ?? process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL;

  const currentUrl = `https://${domainName}${buildUrl(locale, path)}`;

  const organisationJsonLd: WithContext<Organization> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: CHANNEL,
    url: `https://${domainName}/${locale}`,
    logo: navigation?.headerLogo?.imageUrl,
  };

  return (
    <>
      <Head>
        {title && (
          <>
            <title key="title">{title}</title>
            <meta content={title} key="og:title" property="og:title" />
            <meta content={title} key="twitter:title" name="twitter:title" />
          </>
        )}

        <meta content={locale} property="og:locale:alternate" />

        {metaKeywords && <meta content={keywords} name="keywords" />}

        {description && (
          <>
            <meta content={description} key="description" name="description" />
            <meta content={description} key="twitter:description" name="twitter:description" />
            <meta content={description} property="og:description" />
          </>
        )}

        <meta content={titleSuffixContent} property="og:site_name" />

        {currentUrl && (
          <>
            <link href={currentUrl} key="canonical" rel="canonical" />
            <meta content={currentUrl} key="og:url" property="og:url" />
          </>
        )}

        {Object.entries(localizedSlugs)?.map(([locale, path]) => (
          <link href={buildUrl(locale, path)} hrefLang={locale} key={`${locale}-${path}`} rel="alternate" />
        ))}

        {robots && <meta content={robots} name="robots" />}
        {imageSrc && <meta content={imageSrc} property="og:image" />}

        {structuredData && (
          <script dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} type="application/ld+json" />
        )}
        <script dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationJsonLd) }} type="application/ld+json" />
      </Head>
      {children}
    </>
  );
};

export default SEO;
